import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchInventoryStatus } from '../store/slices/inventorySlice';
import { TrendingUp, Package, AlertTriangle, Download, Filter } from 'lucide-react';
import InventoryStatusCard from '../components/inventory/InventoryStatusCard';

const ReportsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { inventoryStatus, reportsLoading } = useAppSelector((state) => state.inventory);

  const [selectedDateRange, setSelectedDateRange] = useState('today');
  const [reportType, setReportType] = useState('inventory');

  useEffect(() => {
    dispatch(fetchInventoryStatus());
  }, [dispatch]);

  const handleExportReport = () => {
    // Export functionality would be implemented here
    alert('Export functionality will be implemented');
  };

  const renderInventoryReport = () => (
    <div className="space-y-6">
      {inventoryStatus && (
        <InventoryStatusCard 
          status={inventoryStatus}
          loading={reportsLoading}
        />
      )}
      
      {/* Additional inventory analytics would go here */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Storage Utilization</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Bins</span>
                <span>{inventoryStatus?.bin_utilization}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${inventoryStatus?.bin_utilization}%` }}
                ></div>
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Racks</span>
                <span>{inventoryStatus?.rack_utilization}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full"
                  style={{ width: `${inventoryStatus?.rack_utilization}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Styles</span>
              <span className="font-medium">{inventoryStatus?.total_styles.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Stock</span>
              <span className="font-medium">{inventoryStatus?.total_stock.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Low Stock Items</span>
              <span className="font-medium text-red-600">{inventoryStatus?.low_stock_alerts.length}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOrderReport = () => (
    <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Order Analytics</h3>
      <div className="text-center py-8 text-gray-500">
        Order reporting features coming soon
      </div>
    </div>
  );

  const renderProductivityReport = () => (
    <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Worker Productivity</h3>
      <div className="text-center py-8 text-gray-500">
        Productivity reporting features coming soon
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600">Monitor inventory, orders, and worker productivity</p>
        </div>
        <button
          onClick={handleExportReport}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700"
        >
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </button>
      </div>

      {/* Controls */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="inventory">Inventory Status</option>
              <option value="orders">Order Analytics</option>
              <option value="productivity">Worker Productivity</option>
            </select>
          </div>
          
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white rounded-md hover:bg-gray-50">
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div>
        {reportType === 'inventory' && renderInventoryReport()}
        {reportType === 'orders' && renderOrderReport()}
        {reportType === 'productivity' && renderProductivityReport()}
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-blue-900">Performance Trends</h3>
              <p className="text-blue-700 text-sm">Stock levels are optimized</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 p-6 rounded-lg border border-green-200">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-green-900">Storage Efficiency</h3>
              <p className="text-green-700 text-sm">Good utilization rates</p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
          <div className="flex items-center">
            <AlertTriangle className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <h3 className="text-lg font-medium text-yellow-900">Attention Required</h3>
              <p className="text-yellow-700 text-sm">
                {inventoryStatus?.low_stock_alerts.length || 0} items need restocking
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
